import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import EmptyState from "../../components/EmptyState";
import StatusChip from "../../components/StatusChip";
import { api, formatApiError } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["queued","planned","in_progress","dismissed","shipped"];

const STATUS_MAP = { queued: "pending", planned: "pending_approval", in_progress: "connecting", dismissed: "not_configured", shipped: "verified" };

export default function FeatureRequests() {
  const [data, setData] = useState({ items: [], counts_by_module: {}, total: 0 });

  const load = async () => {
    try { setData((await api.get("/feature-requests")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const update = async (id, status) => {
    try { await api.patch(`/feature-requests/${id}`, { status }); toast.success(`Status → ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppShell title="Early-Access Requests" subtitle={`${data.total} request(s) · users asking for future modules`}>
      {data.items.length === 0 ? (
        <EmptyState icon={Sparkles} title="No early-access requests" subtitle="Users click Request early access on any PhasePlaceholder screen to signal demand. Requests appear here." />
      ) : (
        <div className="space-y-4">
          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-4" data-testid="fr-module-summary">
            {Object.entries(data.counts_by_module).map(([mod, n]) => (
              <div key={mod} className="bg-white border border-erf-borderCream rounded-md p-3">
                <div className="text-[11px] font-mono uppercase tracking-widest text-erf-dark/60">{mod}</div>
                <div className="font-mono text-2xl text-erf-dark font-semibold">{n}</div>
              </div>
            ))}
          </div>
          <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto">
            <table className="w-full erf-table">
              <thead className="bg-erf-creamSecondary">
                <tr>
                  <th className="text-left px-3 py-2">Module</th>
                  <th className="text-left px-3 py-2 mono">Phase</th>
                  <th className="text-left px-3 py-2">Requested by</th>
                  <th className="text-left px-3 py-2">Reason</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2 mono">Created</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={r.id} className="border-t border-erf-borderCream/60" data-testid={`fr-row-${r.id}`}>
                    <td className="px-3 py-2 mono">{r.module}</td>
                    <td className="px-3 py-2 mono">P{r.phase}</td>
                    <td className="px-3 py-2">
                      <div className="font-mono text-[11px]">{r.user_email}</div>
                      <div className="mono text-[10px] text-erf-dark/50 uppercase tracking-widest">{r.user_role}</div>
                    </td>
                    <td className="px-3 py-2 text-[11px] font-nav max-w-md truncate">{r.reason || "—"}</td>
                    <td className="px-3 py-2">
                      <Select value={r.status} onValueChange={(v) => update(r.id, v)}>
                        <SelectTrigger className="h-7 text-[11px] w-36" data-testid={`fr-status-${r.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2 mono text-[11px]">{(r.created_at || "").slice(0,19).replace("T"," ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AppShell>
  );
}
